import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Mail } from 'lucide-react';

const SkilledWorkerEmploymentVisas = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Skilled Worker and Employment Visas
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Map your role, confirm eligibility, and file from start to finish.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We help professionals and their employers choose a viable route, build a strong evidence pack, and submit a compliant application on time. We regularly handle the United States, Canada, United Kingdom, Germany and wider EU, Australia, and the UAE. Response time is 24-72 hours. Offices in London for EMEA and New York for the APAC desk.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who This Is For */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Who this is for</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "New hires relocating for a sponsored role",
              "Internal transfers or multinational assignments", 
              "Contractors converting to sponsored employment",
              "Founders joining their own company as an employee where the route allows"
            ].map((item, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-6 text-center">
                <Users className="w-8 h-8 text-black mx-auto mb-4" />
                <p className="text-gray-600 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Outcomes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Outcomes you can expect</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: "Written Strategy",
                description: "A written strategy that confirms the best route, fees, and realistic timeline"
              },
              {
                icon: CheckCircle,
                title: "Role Mapping",
                description: "Role mapping to an eligible category with salary threshold checks"
              },
              {
                icon: Shield,
                title: "Complete Application",
                description: "A complete, quality-checked application file ready for submission"
              },
              {
                icon: Calendar,
                title: "Appointment Support",
                description: "Appointment scheduling, biometrics, and interview preparation where needed"
              },
              {
                icon: Globe,
                title: "Next Steps Planning",
                description: "Clear next steps after approval, including renewals and permanent residence planning"
              }
            ].map((outcome, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <outcome.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-acBlack mb-3">{outcome.title}</h3>
                <p className="text-gray-600">{outcome.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Eligibility Basics */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Eligibility basics</h2>
          <div className="space-y-6">
            {[
              "A genuine job offer that meets local salary or market rate rules",
              "Employer sponsorship or equivalent registration where required",
              "Skills and experience that match the role and category",
              "Immigration history that is either clean or explained with credible evidence"
            ].map((requirement, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="w-6 h-6 text-blue-600 mr-4 mt-1 flex-shrink-0" strokeWidth={2.5} />
                <p className="text-gray-600 text-lg">{requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Typical Routes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Typical routes we cover</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                country: "United Kingdom",
                routes: "Skilled Worker, Global Business Mobility routes, Health and Care"
              },
              {
                country: "United States", 
                routes: "L-1 intracompany, O-1 extraordinary ability, H-1B alternatives and cap-exempt options, TN for eligible nationals"
              },
              {
                country: "Canada",
                routes: "LMIA based permits and LMIA exempt categories under International Mobility"
              },
              {
                country: "Germany and EU",
                routes: "EU Blue Card and local work permits"
              },
              {
                country: "Australia",
                routes: "Temporary Skill Shortage (TSS), Employer Nomination Scheme (ENS)"
              },
              {
                country: "UAE",
                routes: "Employer sponsorship and free zone employment visas"
              }
            ].map((route, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-semibold text-acBlack mb-3">{route.country}</h3>
                <p className="text-gray-600">{route.routes}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 italic">
              If your route is not listed here, ask during intake. We can coordinate additional destinations through partners.
            </p>
          </div>
        </div>
      </motion.section>

      {/* What You Get */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-12 text-center">What you get from us</h2>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Strategy and Planning */}
            <div className="bg-blue-50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Strategy and planning</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Route comparison, risk map, and timeline</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Salary, SOC or NOC or ANZSCO mapping and shortage lists where relevant</span>
                </li>
              </ul>
            </div>

            {/* Document Pack */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Document pack and forms</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Tailored checklist for you and your employer</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Templates for employer letters and statements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Certified translation and legalisation guidance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Form completion with dual quality checks</span>
                </li>
              </ul>
            </div>

            {/* Submission */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Submission and follow through</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Online filing or consular filing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Appointment and biometrics scheduling</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Interview coaching and mock questions where relevant</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Status tracking and proactive updates</span>
                </li>
              </ul>
            </div>

            {/* After Approval */}
            <div className="bg-blue-50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">After approval</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Arrival checklist, registrations where applicable, and right to work steps</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Calendar for renewals and reminders</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Permanent residence and citizenship planning when eligible</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Timeline */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-12 text-center">End to end process</h2>
          
          <div className="space-y-8">
            {[
              {
                step: "1",
                title: "Intake and quick screen",
                description: "Short form plus your CV and offer letter. We confirm viability and urgent risks."
              },
              {
                step: "2", 
                title: "Strategy call and written plan",
                description: "Route selected, evidence list agreed, responsibilities assigned, target dates set."
              },
              {
                step: "3",
                title: "Evidence building and forms", 
                description: "We supply templates and examples, review drafts, and run consistency checks across names, dates, roles, and salaries."
              },
              {
                step: "4",
                title: "Submission and appointments",
                description: "We file, book biometrics or VAC, and prepare you for any interview."
              },
              {
                step: "5",
                title: "Decision and onboarding",
                description: "We guide post-approval steps. If refused, we analyse the decision and advise on refile, administrative review, or appeal with licensed counsel where required."
              }
            ].map((process, index) => (
              <div key={index} className="flex items-start">
                <div className="bg-blue-500 text-black rounded-full w-10 h-10 flex items-center justify-center font-bold mr-6 flex-shrink-0">
                  {process.step}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-acBlack mb-2">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Timelines */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Timelines and service levels</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-lg font-semibold text-acBlack mb-2">Planning and pack creation</h3>
              <p className="text-gray-600">Typically 1-3 weeks depending on document readiness</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-lg font-semibold text-acBlack mb-2">Government processing</h3>
              <p className="text-gray-600">Varies by country, season, and premium options</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-lg font-semibold text-acBlack mb-2">Communication</h3>
              <p className="text-gray-600">24-72 hour response time, faster at critical steps</p>
            </div>
          </div>
        </div>
      </motion.section>

  
    </div>
  );
};

export default SkilledWorkerEmploymentVisas;
